<template>
  <article class="dashboard-card preika-insight-card preika-approval-aging">
    <div class="preika-insight-heading">
      <div>
        <p class="preika-insight-heading__eyebrow">APPROVAL FLOW</p>
        <h2>承認滞留分析</h2>
        <p class="preika-insight-heading__description">
          承認待ちの件数と経過日数を表示します。
        </p>
      </div>
      <span class="preika-insight-badge preika-insight-badge--neutral">
        {{ scopeLabel }}
      </span>
    </div>

    <div v-if="loading" class="preika-insight-loading">
      <span class="preika-insight-loading__spinner"></span>
      承認待ち状況を分析中
    </div>

    <div v-else-if="error" class="preika-insight-error" role="alert">
      <div>
        <strong>承認滞留を取得できませんでした</strong>
        <small>承認待ちの申請は従来どおりご利用いただけます。</small>
      </div>
      <button type="button" @click="$emit('retry')">再取得</button>
    </div>

    <template v-else-if="totalCount">
      <!--
        **自分宛てと全社を並べる。**下の［自分の承認待ち一覧］の先
        （承認待ちの申請）は自分宛てしか出さない。全社の数だけを見せると、
        開いたときに「全て承認済みです」になる（束34）。
        字のあいだに空きが入らないよう、1行に詰めて書く。
      -->
      <p class="preika-aging-scope">あなたの承認待ち：<strong>{{ personalCount }}件</strong><template v-if="accountWide">（全社：{{ totalCount }}件）</template></p>
      <div class="preika-aging-kpis">
        <div><small>承認待ち</small><strong>{{ totalCount }}</strong><span>件</span></div>
        <div><small>平均滞留</small><strong>{{ value.averageDays || 0 }}</strong><span>日</span></div>
        <div :class="{ 'preika-aging-kpi--danger': Number(value.oldestDays) >= 8 }">
          <small>最長滞留</small><strong>{{ value.oldestDays || 0 }}</strong><span>日</span>
        </div>
      </div>

      <div class="preika-aging-distribution">
        <div class="preika-aging-distribution__bar" aria-label="承認待ちの滞留日数分布">
          <span
            v-for="segment in segments"
            :key="segment.key"
            :class="'preika-aging-segment--' + segment.tone"
            :style="{ width: segment.width + '%' }"
            :title="segment.label + ': ' + segment.count + '件'"
          ></span>
        </div>
        <div class="preika-aging-legend">
          <span v-for="segment in segments" :key="segment.key">
            <i :class="'preika-aging-legend__dot--' + segment.tone"></i>
            {{ segment.label }} <strong>{{ segment.count }}</strong>
          </span>
        </div>
      </div>

      <div class="preika-aging-list-heading">
        <div><small>OLDEST FIRST</small><strong>古い承認待ち</strong></div>
        <router-link v-if="canOpenOwnList" :to="OWN_LIST">自分の承認待ち一覧<CIcon name="cil-arrow-right"/></router-link>
      </div>

      <!--
        **自分宛ての行は「承認待ちの申請」へ。**そこで中身を見て承認する。
        **他人宛ての行は承認者名を出し、年月と氏名を渡して一覧を開く。**
        以前はどの行も月を渡さずに一覧を開くだけで、承認も付け替えもできなかった。
      -->
      <div class="preika-aging-list">
        <template v-for="item in items">
          <router-link
            v-if="item.assignedToMe"
            :key="item.id"
            :to="OWN_LIST"
            class="preika-aging-item"
          >
            <span class="preika-aging-item__days" :class="agingTone(item.waitingDays)">
              <strong>{{ item.waitingDays }}</strong><small>日</small>
            </span>
            <span class="preika-aging-item__content">
              <strong>{{ item.documentName }}</strong>
              <span>{{ item.userName }}</span>
            </span>
            <span class="preika-aging-item__date">{{ item.submissionAt || '日時未設定' }}</span>
            <CIcon name="cil-chevron-right" class="preika-aging-item__arrow"/>
          </router-link>
          <a
            v-else-if="canOpen(item.route)"
            :key="item.id"
            href="#"
            class="preika-aging-item"
            @click.prevent="openOthers(item)"
          >
            <span class="preika-aging-item__days" :class="agingTone(item.waitingDays)">
              <strong>{{ item.waitingDays }}</strong><small>日</small>
            </span>
            <span class="preika-aging-item__content">
              <strong>{{ item.documentName }}</strong>
              <span>{{ item.userName }}<small v-if="item.approverName">承認者 {{ item.approverName }}</small></span>
            </span>
            <span class="preika-aging-item__date">{{ item.submissionAt || '日時未設定' }}</span>
            <CIcon name="cil-chevron-right" class="preika-aging-item__arrow"/>
          </a>
          <div
            v-else
            :key="item.id"
            class="preika-aging-item"
          >
            <span class="preika-aging-item__days" :class="agingTone(item.waitingDays)">
              <strong>{{ item.waitingDays }}</strong><small>日</small>
            </span>
            <span class="preika-aging-item__content">
              <strong>{{ item.documentName }}</strong>
              <span>{{ item.userName }}<small v-if="item.approverName">承認者 {{ item.approverName }}</small></span>
            </span>
            <span class="preika-aging-item__date">{{ item.submissionAt || '日時未設定' }}</span>
          </div>
        </template>
      </div>

      <!--
        会社全体だと承認待ちが数百件になりうる。全部を描いても読めないので
        古い順に先頭だけ出す。**黙って切らない。** 残りの件数を必ず出す
        （このカードから、残りを全部見られる一覧は開けない。上のリンクの先は
        自分宛てだけ）。
      -->
      <p v-if="hiddenItems > 0" class="preika-aging-more">ほか{{ hiddenItems }}件の承認待ちがあります</p>
    </template>

    <div v-else class="preika-insight-empty">
      <span class="preika-insight-empty__icon"><CIcon name="cil-check-circle"/></span>
      <div>
        <strong>承認待ちはありません</strong>
        <p>現在、滞留している申請はありません。</p>
      </div>
    </div>
  </article>
</template>

<script>
import { canAccessPath } from '../../utils/accessPolicy'
import { openListWith } from '../../utils/dashboardInsights'

const OWN_LIST = '/submission_approval/submission_management'

export default {
  name: 'DashboardApprovalAging',

  props: {
    aging: {
      type: Object,
      default: () => ({})
    },
    /** 本人の役割・会社の機能・権限。行き先を開けるかを左メニューと同じ判定で決める。 */
    accessContext: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    value () {
      return this.aging || {}
    },

    totalCount () {
      return Number(this.value.totalCount || 0)
    },

    scopeLabel () {
      return this.value.scope === 'ACCOUNT' ? '全社' : '自分の承認'
    },

    OWN_LIST () {
      return OWN_LIST
    },

    accountWide () {
      return this.value.scope === 'ACCOUNT'
    },

    /** 自分宛ての件数。上段の「承認待ち」と同じ数え方。 */
    personalCount () {
      const value = Number(this.value.personalCount)
      if (Number.isFinite(value) && this.value.personalCount !== undefined) return value
      return this.accountWide ? 0 : this.totalCount
    },

    canOpenOwnList () {
      return canAccessPath(this.accessContext, OWN_LIST)
    },

    hiddenItems () {
      return Number(this.value.hiddenItems || 0)
    },

    items () {
      return Array.isArray(this.value.items) ? this.value.items : []
    },

    segments () {
      const definitions = [
        { key: 'one', label: '0〜1日', count: Number(this.value.withinOneDayCount || 0), tone: 'fresh' },
        { key: 'three', label: '2〜3日', count: Number(this.value.twoToThreeDaysCount || 0), tone: 'watch' },
        { key: 'seven', label: '4〜7日', count: Number(this.value.fourToSevenDaysCount || 0), tone: 'warning' },
        { key: 'over', label: '8日以上', count: Number(this.value.eightDaysOrMoreCount || 0), tone: 'danger' }
      ]
      return definitions.map(segment => ({
        ...segment,
        width: this.totalCount ? (segment.count / this.totalCount) * 100 : 0
      }))
    }
  },

  methods: {
    canOpen (route) {
      return Boolean(route) && canAccessPath(this.accessContext, route)
    },

    /** 他人宛ての行。年月と申請者の氏名を渡して一覧を開く。 */
    openOthers (item) {
      openListWith(this.$router, item.route, item.yearMonth, item.userName)
    },

    agingTone (days) {
      const value = Number(days || 0)
      if (value >= 8) return 'preika-aging-item__days--danger'
      if (value >= 4) return 'preika-aging-item__days--warning'
      if (value >= 2) return 'preika-aging-item__days--watch'
      return 'preika-aging-item__days--fresh'
    }
  }
}
</script>

<style scoped>
.preika-aging-scope{margin:0 0 12px;color:#4d6477;font-size:12px}
.preika-aging-scope strong{color:#172b3f}
div.preika-aging-item{cursor:default}
</style>
